import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { NGXLogger } from 'ngx-logger';
import { AppConstants } from '../commons/app-constants';
import { LegalService } from '../services/legal.service';
import { NotificationListResponse, UnreadCountResponse } from './notification.model';

/**
 * REST calls + the reactive state (unreadCount$/refresh$) that both the Phase 1 poll and the
 * Phase 2 WebSocket (NotificationSocketService) feed into — components don't need to know which
 * transport delivered an update. "Graceful degrade to nothing shown when the backend flag is off"
 * contract, same as CompanyMembershipService (getMyMemberships returns an empty list rather than
 * erroring when app.feature.multiCompanyMembership is off) — no separate Angular-side flag to
 * keep in sync with the backend one.
 */
@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly logger = inject(NGXLogger);
  private readonly legalService = inject(LegalService);
  private readonly BASE_URL = `${AppConstants.API_URL}api/v1/notifications`;

  private readonly unreadCountSource = new BehaviorSubject<number>(0);
  readonly unreadCount$ = this.unreadCountSource.asObservable();

  // Emits whenever the list should be considered stale (after accept/decline/markRead) so the
  // bell dropdown / /notifications page can refetch without a tight coupling between components.
  private readonly refreshSource = new Subject<void>();
  readonly refresh$ = this.refreshSource.asObservable();

  constructor(private http: HttpClient) {}

  // 2026-09-28 (live updates step 2): startPolling()/stopPolling() and the 30 s REST poll removed — the
  // shared live connection reconciles the count after every (re)connect and when the app comes back.

  /** Called by NotificationSocketService when a real-time message arrives — tells any open
   *  bell dropdown / /notifications page to refetch, same signal accept/decline/markRead already
   *  emit after their REST call succeeds. */
  notifyRefresh(): void {
    this.refreshSource.next();
  }

  refreshUnreadCount(): void {
    this.http.get<UnreadCountResponse>(`${this.BASE_URL}/unread-count`).subscribe({
      next: (res) => this.unreadCountSource.next(res?.enabled ? (res.count ?? 0) : 0),
      error: () => { /* silent — badge just stays at its last known value, matches ticket-badge pattern */ }
    });
  }

  list(page = 0, size = 20): Observable<NotificationListResponse> {
    return this.http.get<NotificationListResponse>(this.BASE_URL, { params: { page, size } as any });
  }

  markRead(id: number): Observable<any> {
    return this.http.post(`${this.BASE_URL}/${id}/read`, {}).pipe(
      tap(() => { this.refreshUnreadCount(); this.refreshSource.next(); })
    );
  }

  markAllRead(): Observable<any> {
    return this.http.post(`${this.BASE_URL}/mark-all-read`, {}).pipe(
      tap(() => { this.unreadCountSource.next(0); this.refreshSource.next(); })
    );
  }

  acceptInvite(id: number, termsAccepted = false): Observable<any> {
    this.logger.debug('[NotificationService] acceptInvite()', id);
    return this.http.post(`${this.BASE_URL}/${id}/accept-invite`, { termsAccepted }).pipe(
      tap(() => { if (termsAccepted) { this.legalService.notifyAcceptancesChanged(); } }), // the app shell re-reads the legal prompt
      tap(() => { this.refreshUnreadCount(); this.refreshSource.next(); })
    );
  }

  declineInvite(id: number): Observable<any> {
    this.logger.debug('[NotificationService] declineInvite()', id);
    return this.http.post(`${this.BASE_URL}/${id}/decline-invite`, {}).pipe(
      tap(() => { this.refreshUnreadCount(); this.refreshSource.next(); })
    );
  }
}
